// M17 T3 — Review "KPI değerleri değişti" bandı (trigger 0057); doğrulandıysa gri tek satır.
import { useState } from "react";
import { formatKpiNumber, type ReviewSection } from "@mahir/shared";
import { KPI_COPY } from "../../lib/kpi-ui";
import { REVIEW_COPY } from "../../lib/review-copy";
import { formatVersionDate } from "../../lib/kpi-cell-history";
import { acknowledgeKpiStale, regenerateSection } from "../../api/projects";

interface Props {
  projectId: string;
  section: ReviewSection;
  /** Final review I2: yerel değişiklik varken yeniden üretim pasif (üretilen metin yazılanla ayrışırdı). */
  hasUnsavedChanges?: boolean;
  onChanged: () => void;
}
const C = KPI_COPY.review;
const SHOWN = 3;
const num = (v: number | null): string => (v === null ? "-" : formatKpiNumber(v));
const BTN = "px-3 py-1.5 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed";
const PRIMARY = { background: "var(--color-primary)", color: "var(--color-surface)" };
const SECONDARY = { border: "1px solid var(--color-border)", background: "var(--color-surface)" };

export function KpiStaleBand({
  projectId,
  section,
  hasUnsavedChanges = false,
  onChanged,
}: Props): JSX.Element | null {
  const [busy, setBusy] = useState<"regenerate" | "acknowledge" | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!section.kpiStale) {
    if (!section.kpiStaleAck) return null;
    const { byName, at } = section.kpiStaleAck;
    return (
      <p
        className="mb-3 text-xs"
        data-testid="kpi-stale-ack"
        style={{ color: "var(--color-text-3)" }}
      >
        {C.acknowledged(byName, formatVersionDate(at))}
      </p>
    );
  }

  const inputs = section.kpiStaleInputs;
  const locked = section.reviewStatus === "approved";
  const generating = ["queued", "running"].includes(section.generationStatus);
  const titleId = `kpi-stale-title-${section.sectionCode}`;
  const run = async (kind: "regenerate" | "acknowledge"): Promise<void> => {
    setBusy(kind);
    setError(null);
    try {
      if (kind === "regenerate") await regenerateSection(projectId, section.sectionCode);
      else await acknowledgeKpiStale(projectId, section.sectionCode);
      onChanged();
    } catch {
      setError(kind === "regenerate" ? C.regenerateFailed : C.acknowledgeFailed);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="mb-3 rounded-md p-3 text-sm"
      role="status"
      data-testid="kpi-stale-band"
      style={{
        background: "var(--color-warning-bg)",
        border: "1px solid var(--color-warning-border)",
        color: "var(--color-text)",
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <strong id={titleId} style={{ color: "var(--color-warning-text)" }}>
            ⚠ {C.staleTitle}
          </strong>
          <p className="mt-1 text-xs" style={{ color: "var(--color-text-2)" }}>
            {inputs.length > 0 ? C.staleLead(inputs.length) : C.staleLeadNoList}
          </p>
          {inputs.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs" style={{ fontFamily: "var(--font-mono)" }}>
              {inputs.slice(0, SHOWN).map((i) => (
                <li key={`${i.label}-${i.year}`}>
                  {C.staleRow(i.label, i.year, num(i.from), num(i.to), i.unit)}
                </li>
              ))}
              {inputs.length > SHOWN && (
                <li style={{ color: "var(--color-text-3)" }}>
                  {C.staleMore(inputs.length - SHOWN)}
                </li>
              )}
            </ul>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={() => void run("regenerate")}
            disabled={locked || generating || hasUnsavedChanges || busy !== null}
            title={hasUnsavedChanges ? REVIEW_COPY.unsavedBlocksAction : undefined}
            className={BTN}
            style={{ ...PRIMARY, fontWeight: 600 }}
          >
            {C.regenerate}
          </button>
          {hasUnsavedChanges && !locked && (
            <span
              className="text-xs"
              data-testid="kpi-stale-unsaved"
              style={{ color: "var(--color-text-3)" }}
            >
              {REVIEW_COPY.unsavedBlocksAction}
            </span>
          )}
          {locked && (
            <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
              {C.regenerateLocked}
            </span>
          )}
          <button
            type="button"
            onClick={() => void run("acknowledge")}
            disabled={busy !== null}
            aria-describedby={titleId}
            className={BTN}
            style={{ ...SECONDARY, color: "var(--color-text)", fontWeight: 600 }}
          >
            {C.acknowledge}
          </button>
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs" style={{ color: "var(--color-warning)" }}>
          {error}
        </p>
      )}
    </div>
  );
}
